import { ShieldCheck, Award, Users, Wrench } from "lucide-react";
import { LeadForm } from "@/components/LeadForm";
import { Seo } from "@/components/Seo";

const VALUES = [
  { icon: Award, title: "Since 1998", text: "We've been cooling South Florida homes and businesses for over 25 years — through every heat wave and hurricane season." },
  { icon: ShieldCheck, title: "Licensed & Insured Contractors", text: "Services are performed by independent, state-licensed and insured HVAC contractors with background-checked technicians. The performing contractor's name and license number appear on your booking confirmation and invoice." },
  { icon: Users, title: "Straight Talk", text: "We diagnose first, quote a flat price, and only start work when you approve it. The $99 diagnostic fee is waived when you proceed with the repair." },
  { icon: Wrench, title: "Full-Service Pros", text: "Compressors, air handlers, heat pumps, ductwork, thermostats and indoor air quality — design, install, maintain and repair." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Central Air Depot | South Florida AC Since 1998" description="Family-run AC service built on referrals since 1998. Honest diagnostics, flat upfront pricing and licensed, insured contractors across South Florida." />
      <section className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-4 py-20 sm:py-28">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">About Central Air Depot</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black max-w-2xl">
            A South Florida company, built on referrals
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">
            Since 1998, Central Air Depot has grown one happy customer at a time. We believe AC service should be simple: show up fast, diagnose honestly, quote a flat price, and do the job right.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} data-testid={`about-value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="border border-zinc-200 p-8 rounded-sm hover:border-brand hover:-translate-y-1 transition-[transform,border-color] duration-200">
              <v.icon className="w-8 h-8 text-brand mb-4" />
              <h2 className="font-heading font-bold text-xl text-brand-ink">{v.title}</h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-subtle">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-6">What we stand for</h2>
            <div className="space-y-4 text-zinc-700 leading-relaxed">
              <p><strong className="text-brand-ink">Free, upfront estimates</strong> for heating and cooling equipment installation — always.</p>
              <p><strong className="text-brand-ink">Trained specialists</strong> equipped with professional-grade parts, leading national brands and extended warranty options on parts, equipment and labor.</p>
              <p><strong className="text-brand-ink">24/7 emergency booking.</strong> Call any time — our system takes your request, and a technician is dispatched after a $200 prepaid emergency booking fee, which is applied toward your repair.</p>
              <p><strong className="text-brand-ink">Service plans for ongoing maintenance,</strong> so your system is cared for before problems start — not after.</p>
            </div>
          </div>
          <LeadForm heading="Talk to a real person" subtext="Questions about your system? Send us a note and we'll call you back." />
        </div>
      </section>
    </div>
  );
}
